import React, { useRef, useState } from 'react';
import classNames from 'classnames';

import Loader from 'components/Loader';
import Icon from 'components/Icon';
import { IMenuDropdownItem, IMenuLink } from 'components/SubNavBar/utils';
import { colors } from 'constants/colors';
import { useOutsideElementClick } from 'hooks/useOutsideElementClick';

import styles from './MenuDropdown.module.scss';

interface Props {
  id: string;
  label: string;
  listItems: IMenuDropdownItem[];
  customHeader?: React.ReactNode;
  gridClassName?: string;
  isLoading?: boolean;
}

export const MenuDropdown: React.FC<Props> = ({
  id,
  label,
  listItems,
  customHeader,
  gridClassName,
  isLoading,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const customHeaderRef = useRef<HTMLDivElement>(null);

  const handleClickLink = (link: IMenuLink) => {
    link.onClick();
    setShowMenu(false);
  };

  useOutsideElementClick([buttonRef, divRef, customHeaderRef], () =>
    setShowMenu(false),
  );

  const renderLinkList = () => {
    if (isLoading) return <Loader />;

    return listItems.map(menu => (
      <div className={menu.classname} key={menu.title}>
        <label className={styles['menu-title']}>{menu.title}</label>
        {menu.links.map(link => (
          <button
            key={link.label}
            onClick={() => handleClickLink(link)}
            className={classNames(styles['menu-item'], {
              [styles['menu-item-disabled']]: link.disabled,
            })}
            disabled={link.disabled}
          >
            {link.label}
          </button>
        ))}
      </div>
    ));
  };

  return (
    <>
      <button
        className={classNames(
          styles['button-label'],
          showMenu && styles['opened-menu'],
        )}
        onClick={() => setShowMenu(!showMenu)}
        ref={buttonRef}
      >
        {label}
        <Icon
          name={showMenu ? 'arrowUp' : 'arrowDown'}
          width={0.6625}
          height={0.6875}
          color={colors.primaryColor500}
        />
      </button>
      {showMenu && (
        <>
          <div ref={customHeaderRef}>{customHeader}</div>
          <div
            className={classNames(styles['menu-container'], gridClassName)}
            ref={divRef}
            id={id}
          >
            <>{renderLinkList()}</>
          </div>
        </>
      )}
    </>
  );
};
